import { addDays, isAfter } from "date-fns";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { formatErrorString } from "../../utils/format";
import type { DocumentWithRelations } from "../../../../api/src/types";

export const getNotarizationInfoMessage = ({
  isAttorneyRequiredUserA,
  isAttorneyRequiredUserB,
  isAttorneyABooked,
  isAttorneyBBooked,
  isAttorneyApproved,
  isDocumentGenerated,
  isDocumentReviewed,
  isDocumentFinalized,
  error,
  isUserA,
  partnerName,
}: {
  isAttorneyRequiredUserA?: boolean;
  isAttorneyRequiredUserB?: boolean;
  isAttorneyABooked?: boolean;
  isAttorneyBBooked?: boolean;
  isAttorneyApproved?: boolean;
  isDocumentGenerated?: boolean;
  isDocumentReviewed?: boolean;
  isDocumentFinalized?: boolean;
  error?: FetchBaseQueryError | SerializedError | undefined;
  isUserA?: boolean;
  partnerName?: string;
}): {
  title?: string;
  message: string;
  button?: {
    text: string;
    link: string;
  };
} | null => {
  if (error) {
    return {
      message: formatErrorString(error),
    };
  }

  if (!isDocumentGenerated) {
    return {
      title:
        "Notarization will be available after you have generated a document.",
      message: "You must generate a document before you can notarize it.",
      button: {
        text: "Go to generate document",
        link: "/documents",
      },
    };
  }

  const doesUserHaveAttorney = isUserA
    ? isAttorneyRequiredUserA && !isAttorneyABooked
    : isAttorneyRequiredUserB && !isAttorneyBBooked;

  const doesPartnerHaveAttorney = isUserA
    ? isAttorneyRequiredUserB && !isAttorneyBBooked
    : isAttorneyRequiredUserA && !isAttorneyABooked;

  if (doesUserHaveAttorney) {
    return {
      title:
        "Notarization will be available after you have met with your attorney.",
      message:
        "You’re almost done! You must book an attorney before you can notarize your document.",
      button: {
        text: "Go to book attorney",
        link: "/attorney-services",
      },
    };
  }

  if (doesPartnerHaveAttorney) {
    return {
      title:
        "Notarization will be available after your partner has met with their attorney.",
      message: `You’re almost done! ${partnerName} has not booked an attorney for representation yet.
After they have met with their attorney, you’ll receive an email to begin notarization.`,
    };
  }

  if (
    isAttorneyRequiredUserA &&
    isAttorneyABooked &&
    isAttorneyRequiredUserB &&
    isAttorneyBBooked &&
    !isAttorneyApproved
  ) {
    return {
      title:
        "Notarization will be available after your attorneys have approved your document.",
      message:
        "Your attorneys must approve your document before you can notarize.",
      button: {
        text: "Go to document",
        link: "/documents",
      },
    };
  }

  // Allow notarization if document is reviewed (self-service) OR finalized (attorney-assisted)
  if (!isDocumentReviewed && !isDocumentFinalized) {
    return {
      title:
        "Notarization will be available after you have reviewed your document.",
      message: "You must review your document before you can notarize it.",
      button: {
        text: "Go to review document",
        link: "/documents/review",
      },
    };
  }

  return null;
};

export const getSevenDayRuleEndDate = (finalizedAt: string | Date) => {
  return addDays(new Date(finalizedAt), 7);
};

export const getIsSevenDayRuleOver = (finalizedAt: string | Date) => {
  return isAfter(new Date(), getSevenDayRuleEndDate(finalizedAt));
};

export const is7DayRuleEnabled = (
  document: DocumentWithRelations | undefined | null,
  isAttorneyRequired: boolean,
): boolean => {
  if (!document || document.state.abbreviation !== "CA") return false;

  if (isAttorneyRequired) {
    return Boolean(
      document.finalizedAt && !getIsSevenDayRuleOver(document.finalizedAt),
    );
  } else {
    return Boolean(
      document.reviewedAt && !getIsSevenDayRuleOver(document.reviewedAt),
    );
  }
};
